/**
 * Renders the column-field cardinality state next to the column picker.
 *
 *   loading   → spinner + "Checking cardinality…"
 *   warning   → amber chip with distinct count, warning text, and suggestions
 *               (data-testid="cardinality-warning")
 *   ok        → green ✓ chip with distinct count
 *   no data   → nothing
 *
 * Wrike #4374772881: extracted from StepGrouping so the warning branch can be
 * unit-tested without the surrounding tRPC/Zustand wiring.
 */

interface CardinalityData {
	distinctCount: number
	warning?: string
	suggestions?: string[]
}

interface CardinalityDisplayProps {
	isLoading: boolean
	data: CardinalityData | undefined
}

export function CardinalityDisplay({ isLoading, data }: CardinalityDisplayProps) {
	if (isLoading) {
		return (
			<div className='flex items-center gap-2 text-xs text-purple-600'>
				<svg className='w-4 h-4 animate-spin' viewBox='0 0 24 24' aria-hidden='true'>
					<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
					<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
				</svg>
				Checking cardinality...
			</div>
		)
	}
	if (!data) return null
	if (data.warning) {
		return (
			<div
				data-testid='cardinality-warning'
				role='alert'
				aria-live='polite'
				className='text-xs p-2 rounded bg-amber-50 text-amber-800 border border-amber-200'>
				<div className='font-medium'>{data.distinctCount} unique values</div>
				<div className='mt-1'>{data.warning}</div>
				{data.suggestions && data.suggestions.length > 0 && (
					<ul className='mt-1 list-disc list-inside'>
						{data.suggestions.map((s) => (
							<li key={s}>{s}</li>
						))}
					</ul>
				)}
			</div>
		)
	}
	return (
		<div className='text-xs p-2 rounded bg-green-50 text-green-700 border border-green-200'>
			<div className='font-medium'>{data.distinctCount} unique values ✓</div>
		</div>
	)
}
